import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';

import { DialogCloseEvent, DialogOpenEvent, DialogComponent } from '@theme/dialog';
import { getProvider, SHOPIFY_NATIVE } from '@theme/search-providers';
import { SearchUpdateEvent } from '@shopify/events';

const POPULAR_WORKER_URL = 'https://powerlete-popular-searches.powerleteclo.workers.dev';

function trackSearch(term, market) {
  const trimmed = (term || '').trim();
  if (trimmed.length < 2) return;
  // Thread the shopper's Shopify market handle so the worker can dual-write a per-market bucket
  // (it only does so for allowlisted markets; the field is harmless otherwise). Omit when blank
  // so the payload stays identical to before for any context that has no market.
  const payload = { query: trimmed.toLowerCase() };
  if (market) payload.market = market;
  navigator.sendBeacon(
    `${POPULAR_WORKER_URL}/track`,
    new Blob([JSON.stringify(payload)], { type: 'application/json' })
  );
}

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  #emptyStateLoaded = false;

  /**
   * Cached worker popular-search terms (or null). Fetched once per page-load and re-applied
   * after every #resetSearch morph, so the dynamic chips survive panel close / query-clear
   * instead of being reconciled back to the curated chips and never returning.
   * @type {string[] | null}
   */
  #popularTerms = null;

  /**
   * Curated Popular Searches terms, used as the last-resort source for the "Search suggestions"
   * row so it never disappears once a query is run (Oner parity — suggestions stay visible "all the
   * time"). Captured synchronously from the server-rendered empty-state chips on first open, then
   * refreshed with the live worker terms when they arrive.
   * @type {Array<{text: string, url: string}>}
   */
  #popularFallbackTerms = [];

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal, once: true });
      dialog.addEventListener(DialogOpenEvent.eventName, () => {
        this.refs.searchInput.setAttribute('aria-expanded', 'true');
        // Move initial focus to the query field. showModal() itself now lands on the dialog's
        // hidden heading (autofocus, snippets/search-modal.liquid), because this script is
        // interaction-gated and is not loaded yet on the first open of a page on touch devices, so
        // this handler cannot be what keeps focus off the strip's hamburger on that first open.
        // Before the heading had autofocus, showModal() landed on the burger and left a stray
        // focus-visible box on it. iOS Safari can silently REJECT programmatic input focus
        // outside a user gesture and bounce focus back to the burger, so if focus is still on a
        // strip control afterwards, blur it — that guarantees no box, keeps the keyboard closed
        // (so the browse-first empty state stays visible), and runs in the same frame as showModal
        // (dispatch is synchronous), so the box never paints. Matches Oner (no box).
        this.refs.searchInput.focus();
        const active = document.activeElement;
        if (active instanceof HTMLElement && active.closest('.pw-search-modal-header')) {
          active.blur();
        }
      }, { signal });

      this.addEventListener('click', this.#handleModalClick, { signal });
    }

    // The empty state (with recently-viewed prepended) is loaded lazily on the
    // first dialog open (see #handleDialogOpen), so no predictive request fires
    // on a page where the shopper never opens search.
  }

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);

    // Track popular search chip clicks (static <a> links in the empty-state block)
    const pill = target.closest('.pills__pill');
    if (pill instanceof HTMLAnchorElement && pill.closest('.pw-search-block')) {
      const url = new URL(pill.href, location.origin);
      const term = url.searchParams.get('q');
      if (term) trackSearch(term, this.dataset.market);
    }

    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.refs.searchInput.setAttribute('aria-expanded', 'false');
    this.#resetSearch();
  };

  #handleDialogOpen = () => {
    if (!this.#emptyStateLoaded && RecentlyViewed.getProducts().length > 0) {
      this.#loadEmptyState();
    }
    this.#fetchAndInjectPopularSearches();
  };

  async #fetchAndInjectPopularSearches() {
    // Baseline: capture the server-rendered Popular Searches chips synchronously, BEFORE a search
    // morphs the empty state away, so the suggestions fallback (#popularFallbackQueries) always has
    // terms even if the worker fetch is slow or fails.
    this.#capturePopularFallbackTerms();
    try {
      // Per-market mode (off by default) asks the worker for this shopper's market list; it falls
      // back to the global list server-side when the market has not yet earned its own. Global mode
      // hits the bare /popular endpoint exactly as before.
      let endpoint = `${POPULAR_WORKER_URL}/popular`;
      if (this.dataset.popularScope === 'market' && this.dataset.market) {
        endpoint += `?market=${encodeURIComponent(this.dataset.market)}`;
      }
      const res = await fetch(endpoint);
      if (!res.ok) return;
      const terms = await res.json();
      // Empty / malformed payload → keep the server-rendered curated chips.
      if (!Array.isArray(terms) || terms.length === 0) return;
      // Refresh the suggestions-row fallback with the live worker terms (fresher than the server list).
      this.#popularFallbackTerms = terms
        .filter((term) => typeof term === 'string' && term.trim())
        .map((term) => ({
          text: term.trim(),
          url: `${Theme.routes.search_url}?q=${encodeURIComponent(term.trim())}`,
        }));
      // Cache the worker terms so the Popular COLUMN chips can be re-applied after every
      // #resetSearch morph (they would otherwise reconcile back to the curated markup on close/clear).
      this.#popularTerms = terms;
      this.#injectPopularSearches();
    } catch {
      // silent — static menu chips remain as fallback
    }
  }

  /**
   * Write the cached worker popular terms into the Popular Searches column, replacing the
   * server-rendered curated chips. Idempotent and safe to call repeatedly: it re-reads the
   * cached terms and rewrites the column, so it can be re-applied after #resetSearch's morph
   * reconciles the column back to the curated markup. No-op when no terms are cached or the
   * Popular column is absent — both fail closed to the curated chips. Targets the explicit
   * [data-pw-popular] hook so it can never overwrite the Useful Pages column.
   */
  #injectPopularSearches() {
    const terms = this.#popularTerms;
    if (!Array.isArray(terms) || terms.length === 0) return;
    const chipsEl = this.querySelector('[data-pw-popular] .pw-search-chips');
    if (!chipsEl) return;
    const dummy = document.createElement('span');
    chipsEl.innerHTML = terms
      .map((term) => {
        dummy.textContent = term;
        const safe = dummy.innerHTML;
        const url = `${Theme.routes.search_url}?q=${encodeURIComponent(term)}`;
        return `<li><a class="pills__pill" href="${url}">${safe}</a></li>`;
      })
      .join('');
    const heading = chipsEl.closest('[data-pw-popular]')?.querySelector('.predictive-search-results__title');
    if (heading) {
      heading.textContent = heading.textContent.replace(/\(\d+\)/, `(${terms.length})`);
    }
  }

  /**
   * Capture the server-rendered Popular Searches chips (metaobject/menu source, market-aware) into
   * the suggestions fallback list. Reads only the FIRST .pw-search-chips block (the Popular column;
   * Useful Pages is second), matching how #fetchAndInjectPopularSearches targets it. Synchronous and
   * DOM-only — no extra request. No-op when the chips are not in the DOM (the worker refresh covers
   * that case).
   */
  #capturePopularFallbackTerms() {
    const chipsEl = this.querySelector('.pw-search-block .pw-search-chips');
    if (!chipsEl) return;
    const terms = Array.from(chipsEl.querySelectorAll('a[href]'))
      .map((link) => {
        // Keep only true SEARCH chips (Popular Searches link to ?q=<term>). This excludes a Useful
        // Pages column that could sit first when no Popular source is configured (its links point at
        // pages, not searches), and yields the real search term (decoded) for the re-run on click.
        try {
          const query = new URL(link.href, location.origin).searchParams.get('q');
          return query?.trim() ? { text: query.trim(), url: link.href } : null;
        } catch {
          return null;
        }
      })
      .filter(Boolean);
    if (terms.length) this.#popularFallbackTerms = terms;
  }

  /**
   * Build query-suggestion chips from the curated Popular Searches terms, in the shape
   * #injectQuerySuggestions expects. Used only when neither the provider categories nor the native
   * query suggestions returned any terms, so the "Search suggestions" row stays populated (Oner
   * parity). The terms carry no <mark> match (they are not refinements of the typed query), so the
   * whole chip renders at the muted weight; clicking one re-runs the search for that term in-panel.
   * @returns {Array<{text: string, styled_text: string, url: string}>}
   */
  #popularFallbackQueries() {
    if (!this.#popularFallbackTerms.length) this.#capturePopularFallbackTerms();
    return this.#popularFallbackTerms.slice(0, 5).map((term) => ({
      text: term.text,
      styled_text: this.#highlightTerm(term.text, ''),
      url: term.url,
    }));
  }

  #loadEmptyState() {
    if (this.#emptyStateLoaded) return;
    this.#emptyStateLoaded = true;
    this.resetSearch(false);
  }

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      .filter((item) => item instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) {
      // No options to navigate — drop any stale active-descendant pointer.
      this.refs.searchInput?.removeAttribute('aria-activedescendant');
      return;
    }

    let activeItem = null;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');
        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        activeItem = item;
      } else {
        item.removeAttribute('aria-selected');
      }
    }

    // Point the combobox input at the active OPTION so screen readers announce the roving selection
    // (ARIA combobox pattern). Only the query/category chips are valid options (role="option" inside
    // a listbox, carrying pw-pill-N ids); product cards are NOT options, so pointing the combobox at
    // them would break the role contract — for those we keep just the visual keyboard-focus +
    // aria-selected the base setter already applies, and clear any stale descendant pointer.
    if (activeItem && activeItem.getAttribute('role') === 'option') {
      if (!activeItem.id) activeItem.id = `pw-search-option-${index}`;
      this.refs.searchInput.setAttribute('aria-activedescendant', activeItem.id);
    } else {
      this.refs.searchInput.removeAttribute('aria-activedescendant');
    }

    activeItem?.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }

    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'Tab':
        if (event.shiftKey) {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        } else {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        }
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
        } else {
          const searchUrl = new URL(Theme.routes.search_url, location.origin);
          const term = this.refs.searchInput.value;
          searchUrl.searchParams.set('q', term);
          trackSearch(term, this.dataset.market);
          window.location.href = searchUrl.toString();
        }
        break;
      }
    }
  };

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;

    const allRecentlyViewedElements = [...(recentlyViewedItems || []), ...(recentlyViewedTitle || [])];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (recentlyViewedWrapper) {
      // WHERE FOCUS GOES. PATCHED 2026-09-12, WCAG 2.2 SC 2.4.3. See HORIZON-PATCHES.md before a
      // Horizon upgrade.
      //
      // The button that triggers this sits INSIDE recentlyViewedWrapper, so removing the wrapper
      // destroys the pressed control. The browser then has nowhere to put focus and drops it on
      // <body>, returning a keyboard or screen-reader user to the top of the document with the
      // search panel still open around them and no announcement of what happened.
      //
      // Focus moves to the search input: the one control in this panel that always survives, and
      // where someone who has just cleared their history is most likely to type next. Moved BEFORE
      // the removal rather than in the animation callback, because focusing after the node has gone
      // is a race, and focusing something still animating out targets an element about to die.
      this.refs.searchInput?.focus();

      recentlyViewedWrapper.classList.add('removing');

      onAnimationEnd(recentlyViewedWrapper, () => {
        recentlyViewedWrapper.remove();
      });
    }
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce((event) => {
    // If the input is not a text input (like using the Escape key), don't search
    if (!event.inputType) return;

    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();

    // Require at least 2 characters before firing a section request: kills the
    // per-keystroke request storm (1-character queries issue zero predictive fetches).
    if (searchTerm.length < 2) return;

    this.#getSearchResults(searchTerm);
  }, 200);

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner')?.scrollTo(0, 0);
      this.querySelector('.predictive-search-form__content')?.scrollTo(0, 0);
    });
  }

  /**
   * Fetch search results and update the results container.
   *
   * The active search PROVIDER resolves the term to product IDs — or the
   * SHOPIFY_NATIVE sentinel, meaning "let the native predictive_search render do the
   * search" (the default). The component owns the single render path below — URL
   * build, section render, the post-resolve staleness guard, morph, and the
   * #activeFetch-scoped aria-busy — for EVERY provider, so the Shopify path is exactly
   * the current behaviour and a new provider only has to return data.
   *
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const { predictiveSearchResults } = this.refs;

    const abortController = this.#createAbortController();

    // Dim + mark the results region busy while the (debounced) fetch is in flight.
    predictiveSearchResults.setAttribute('aria-busy', 'true');

    // Horizon 4.3's Shopify standard search event, one per search the shopper runs (Matt's call,
    // 2026-10-24). Settled in `finally` with the rendered result count, or rejected when a newer
    // search supersedes this one, as stock 4.3 does. Our own no-op catch stops each superseded
    // keystroke logging an "Uncaught (in promise)" error; listeners still see the rejection.
    const deferredPromise = SearchUpdateEvent.createPromise();
    deferredPromise.promise.catch(() => {});
    this.dispatchEvent(new SearchUpdateEvent({ search: { query: searchTerm }, promise: deferredPromise.promise }));
    let totalCount = 0;
    /** @type {unknown} */
    let searchError;

    // Parallel query-suggestion fetch — kicked off below when an external provider is active.
    // External providers (e.g. Searchanise) call the Shopify section with an id: query, so
    // predictive_search.resources.queries is always empty. This parallel native fetch restores
    // the suggestion chips that the native path would otherwise show.
    let querySuggestionsPromise = null;

    try {
      let providerResult;
      try {
        // In the theme editor, always use native search so merchants see deterministic results
        // (and the cross-origin provider fetch can't add CSP/CORS noise to the editor). Also gate the
        // provider to the languages its index covers — other storefront languages fall back to native
        // rather than returning silent zero results.
        let providerName = window.Shopify?.designMode ? 'shopify' : this.dataset.searchProvider;
        if (providerName !== 'shopify' && !this.#providerCoversLocale()) {
          providerName = 'shopify';
        }
        if (providerName !== 'shopify') {
          querySuggestionsPromise = this.#fetchQuerySuggestions(searchTerm, abortController.signal).catch(
            () => []
          );
        }
        const provider = await getProvider(providerName, this.dataset.searchApiKey);
        if (abortController.signal.aborted) return;
        providerResult = await provider.getProductIds(searchTerm, { signal: abortController.signal });
      } catch (providerError) {
        // Provider down / bad key / malformed response: never break search for the shopper —
        // fall back to native Shopify results. (An abort is not a real error.)
        if (abortController.signal.aborted || providerError?.name === 'AbortError') return;
        console.warn('[predictive-search] search provider failed, using Shopify', providerError);
        providerResult = SHOPIFY_NATIVE;
        querySuggestionsPromise = null; // native section handles its own query suggestions
      }
      if (abortController.signal.aborted) return;

      // Genuine zero results from a provider → render the provider's own (localized) no-results with
      // the real term, rather than falling back to native (which could surface products the provider
      // intentionally didn't return). Provider ERRORS still fall back to native, above.
      if (providerResult !== SHOPIFY_NATIVE && providerResult.ids.length === 0) {
        await this.#renderProviderNoResults(searchTerm, abortController.signal);
        return;
      }

      const { sectionId, url } = this.#buildResultsRender(searchTerm, providerResult);

      // Read-through cache (keyed by URL, so each market stays distinct); skipped in the theme
      // editor so design-mode edits stay fresh. The abort signal is NOT threaded into
      // getSectionHTML — it de-dupes by URL, so aborting a shared promise would poison an
      // unrelated caller; staleness is guarded post-resolve instead.
      const resultsMarkup = await sectionRenderer.getSectionHTML(
        sectionId,
        !window.Shopify?.designMode,
        url
      );

      if (!resultsMarkup) return;
      if (abortController.signal.aborted) return;

      morph(predictiveSearchResults, resultsMarkup);
      this.#resetScrollPositions();

      // The dedicated section renders only the product grid from the id: query — it can't know the
      // human term or the true total. Inject the "Results for X (total)" meta + the live status
      // announcement client-side, from the provider's data.
      if (providerResult !== SHOPIFY_NATIVE) {
        this.#renderProviderChrome(searchTerm, providerResult);

        // Suggestion chips: source them from the provider's CATEGORIES (clean, relevance-sorted
        // collections — product-type-aware, e.g. "High Support Sports Bras"), NOT the noisy n-gram
        // `suggestions` array (which leaked SKUs + concatenation artifacts). Clicking a chip re-runs
        // the search in-panel (see #activateSuggestion), so it behaves like a refined search, not a
        // collection link. FALL BACK to Shopify-native query suggestions (fetched in parallel) only
        // when the provider returns no categories, so a category-less query still shows chips.
        let queries = this.#normalizeProviderCategories(providerResult.categories, searchTerm);
        if (!queries.length && querySuggestionsPromise) {
          queries = await querySuggestionsPromise;
        }
        // Always-on suggestions (Oner parity): if neither the provider categories nor the native
        // query suggestions yielded any terms, fall back to the curated Popular Searches so the
        // "Search suggestions" row never disappears once a query is run.
        if (!queries.length) {
          queries = this.#popularFallbackQueries();
        }
        if (!abortController.signal.aborted && queries.length) {
          this.#injectQuerySuggestions(queries);
        }
      }

      // Count all result items (products, collections, pages, articles, queries), as stock 4.3 does.
      totalCount = predictiveSearchResults.querySelectorAll('[ref="resultsItems[]"]').length;
    } catch (error) {
      if (abortController.signal.aborted) return;
      searchError = error;
      throw error;
    } finally {
      // Only the latest fetch clears the busy flag (a superseded fetch must not
      // clear the flag the newer one just set).
      if (this.#activeFetch === abortController) {
        predictiveSearchResults.removeAttribute('aria-busy');
      }
      if (abortController.signal.aborted) deferredPromise.reject(new Error('Fetch aborted by user'));
      else if (searchError) deferredPromise.reject(searchError);
      else deferredPromise.resolve({ totalCount });
    }
  }

  /**
   * Resolve which section + URL render the active provider's result.
   *
   * SHOPIFY_NATIVE → the native predictive-search section + predictive_search URL (the current,
   * default behaviour; the search runs server-side during the render). A provider that returns
   * product IDs → the dedicated `predictive-search-results` section with an `id:` query, so the
   * products render through the SAME Liquid card (money / i18n / quick-add reused). The URL is
   * built from the LOCALIZED search route so cards resolve in the active market's currency + locale.
   *
   * @param {string} searchTerm - The term to search for
   * @param {typeof SHOPIFY_NATIVE | { ids: number[] }} providerResult - the active provider's result
   * @returns {{ sectionId: string, url: URL }}
   */
  #buildResultsRender(searchTerm, providerResult) {
    if (providerResult === SHOPIFY_NATIVE) {
      const url = new URL(Theme.routes.predictive_search_url, location.origin);
      url.searchParams.set('q', searchTerm);
      url.searchParams.set('resources[limit_scope]', 'each');
      // 9 fills the desktop 3-up results grid exactly (3 complete rows); the mobile 2-up grid
      // trims the odd trailing card via CSS (see predictive-search-styles). Shopify caps this at 10.
      url.searchParams.set('resources[limit]', '9');
      return { sectionId: this.dataset.sectionId, url };
    }

    // Provider IDs → dedicated results section. The section reads the id: order back from
    // search.terms to preserve the provider's relevance order.
    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', providerResult.ids.map((id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');
    return { sectionId: 'predictive-search-results', url };
  }

  /**
   * Whether the active storefront language is one the selected provider's index covers
   * (the search_provider_languages setting). Other languages fall back to native search so a
   * provider that only indexed English never returns silent zero results in another market.
   * @returns {boolean}
   */
  #providerCoversLocale() {
    const languages = (this.dataset.providerLanguages ?? '')
      .split(',')
      .map((code) => code.trim().toLowerCase())
      .filter(Boolean);
    if (languages.length === 0) return true; // unset = no language gate
    const active = (this.dataset.language ?? '').trim().toLowerCase();
    return languages.includes(active);
  }

  /**
   * Replace @@TOKEN@@ placeholders in a localized template (rendered into a data-* attribute on
   * the component by Liquid, so the strings stay translatable).
   * @param {string | undefined} template
   * @param {Record<string, string | number>} replacements
   * @returns {string}
   */
  #fillTemplate(template, replacements) {
    let out = template ?? '';
    for (const [token, value] of Object.entries(replacements)) {
      out = out.replaceAll(token, String(value));
    }
    return out;
  }

  /**
   * Inject the provider-results chrome the dedicated section can't render (it only knows the id:
   * query, not the human term): the live-region status announcement + the "Results for X (total)"
   * meta row with a "View all results" link to the native results page.
   * @param {string} term - The human search term
   * @param {{ total: number }} result - The provider result
   */
  #renderProviderChrome(term, result) {
    // When Searchanise auto-corrects the query (e.g. "leggins" → "leggings"),
    // use the corrected term for display and the VIEW ALL link so the customer
    // seamlessly sees results for the right word without noticing the typo.
    const displayTerm = result.corrected || term;

    const status = this.querySelector('#pw-results-status');
    if (status) {
      status.textContent = this.#fillTemplate(this.dataset.statusTemplate, {
        '@@N@@': result.total,
        '@@Q@@': displayTerm,
      });
    }

    const inner = this.querySelector('.predictive-search-results__inner');
    if (!inner || inner.querySelector('.pw-search-meta')) return;

    const meta = document.createElement('div');
    meta.className = 'pw-search-meta';

    const label = document.createElement('span');
    label.className = 'pw-search-meta__label';
    label.textContent = `${this.#fillTemplate(this.dataset.metaTemplate, { '@@Q@@': displayTerm })} (${result.total})`;

    const viewAll = document.createElement('a');
    viewAll.className = 'pw-search-meta__viewall';
    viewAll.href = `${Theme.routes.search_url}?q=${encodeURIComponent(displayTerm)}`;
    viewAll.textContent = this.dataset.viewAllLabel ?? '';

    meta.append(label, viewAll);
    // Place the meta above the grid, after the visually-hidden status node.
    inner.insertBefore(meta, status?.nextSibling ?? inner.firstChild);
  }

  /**
   * Render the provider's localized no-results state (with the real term) when it returns zero
   * products. Instead of dead-ending on a bare message, it pulls in the curated empty-state content
   * (Popular Searches / Useful Pages / "You might also like") below the message so the shopper always
   * has somewhere to go (Baymard: no-results should never be a dead end). Falls back to the bare
   * message if the empty section can't be fetched. The term is set as text (never HTML).
   * @param {string} term - The human search term
   * @param {AbortSignal} [signal] - aborts if a newer search supersedes this one mid-fetch
   */
  async #renderProviderNoResults(term, signal) {
    const { predictiveSearchResults } = this.refs;
    const message = this.#fillTemplate(this.dataset.noResultsTemplate, { '@@Q@@': term });

    try {
      const url = new URL(window.location.href);
      url.searchParams.delete('page');
      // Fetch uncached, mirroring #resetSearch's fetch of the same empty section.
      const emptyMarkup = await sectionRenderer.getSectionHTML('predictive-search-empty', false, url);
      if (signal?.aborted) return;

      const parsedEmpty = new DOMParser()
        .parseFromString(emptyMarkup, 'text/html')
        .querySelector('.predictive-search-empty-section');
      const inner = parsedEmpty?.querySelector('.predictive-search-results__inner');

      if (parsedEmpty && inner) {
        // Lead with the no-results message + a live-region status, then the curated blocks already
        // in the empty section. textContent only — the term can never inject markup.
        const status = document.createElement('div');
        status.id = 'pw-results-status';
        status.className = 'visually-hidden';
        status.setAttribute('role', 'status');
        // Leave the live region EMPTY before it enters the DOM; its text is set AFTER morph below.
        // A role="status" region inserted already-populated is not reliably announced — the message
        // must be a content CHANGE on a region already in the a11y tree (matches the fallback path).

        const paragraph = document.createElement('p');
        paragraph.className = 'predictive-search-results__no-results predictive-search-results__no-results--lead';
        paragraph.textContent = message;

        inner.prepend(paragraph);
        inner.prepend(status);

        morph(predictiveSearchResults, parsedEmpty);
        const liveStatus = this.querySelector('#pw-results-status');
        if (liveStatus) liveStatus.textContent = message;
        this.#resetScrollPositions();
        return;
      }
    } catch {
      // fall through to the bare message below
    }
    if (signal?.aborted) return;

    // Fallback: the original bare no-results message.
    morph(
      predictiveSearchResults,
      '<div id="predictive-search-results" class="predictive-search-dropdown">' +
        '<div class="predictive-search-results__inner" data-search-results>' +
        '<div id="pw-results-status" class="visually-hidden" role="status"></div>' +
        '<p class="predictive-search-results__no-results"></p>' +
        '</div></div>'
    );
    const status = this.querySelector('#pw-results-status');
    const paragraph = this.querySelector('.predictive-search-results__no-results');
    if (status) status.textContent = message;
    if (paragraph) paragraph.textContent = message;

    this.#resetScrollPositions();
  }

  /**
   * Fetch native Shopify query suggestions for the given term.
   * Used alongside external providers so typed-prefix chips appear even though
   * the Shopify section is called with an id: query (which returns no suggestions).
   * @param {string} term
   * @param {AbortSignal} signal
   * @returns {Promise<Array<{text: string, styled_text: string, url: string}>>}
   */
  async #fetchQuerySuggestions(term, signal) {
    // Build from the active locale's search route so suggestions come from the shopper's market
    // (e.g. /en-gb/search/suggest.json), not always the primary locale. Fall back to the root path.
    const searchBase = window.Theme?.routes?.search_url || '/search';
    const url = new URL(`${searchBase}/suggest.json`, window.location.origin);
    url.searchParams.set('q', term);
    url.searchParams.set('resources[type]', 'query');
    url.searchParams.set('resources[limit]', '5');
    const response = await fetch(url.toString(), { signal });
    if (!response.ok) return [];
    const data = await response.json();
    return data?.resources?.results?.queries ?? [];
  }

  /**
   * Normalize the provider's CATEGORY suggestions (collection title + link) into the same
   * {text, styled_text, url} shape the native suggest API returns, so #injectQuerySuggestions has a
   * single render path. The chip label is the collection title (clean + product-type-aware); the url
   * is a SEARCH url for that title (so clicking re-runs a search — see #activateSuggestion — and a
   * modified/middle click still opens results in a new tab), NOT the collection page. Near-duplicate
   * SEO collections (e.g. "Straight Leg Joggers Womens" / "...Ladies") are deduped, then capped at 5.
   * @param {Array<{title: string, link: string}> | undefined} categories
   * @param {string} term - the human search term (for highlighting)
   * @returns {Array<{text: string, styled_text: string, url: string}>}
   */
  #normalizeProviderCategories(categories, term) {
    if (!Array.isArray(categories)) return [];
    const seen = new Set();
    const out = [];
    for (const category of categories) {
      const text = (category?.title ?? '').trim();
      if (!text) continue;
      const key = this.#suggestionDedupeKey(text);
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({
        text,
        styled_text: this.#highlightTerm(text, term),
        url: `${Theme.routes.search_url}?q=${encodeURIComponent(text)}`,
      });
      if (out.length >= 5) break;
    }
    return out;
  }

  /**
   * Build a dedupe key that collapses ONLY true SEO duplicates — the same phrase differing by a
   * gender synonym or word order ("Straight Leg Joggers Womens" / "...Ladies" / "Women's Straight Leg
   * Joggers"). It canonicalises gender SYNONYMS (womens/ladies/woman → "women", mens/man → "men") so
   * those variants match, but keeps "women" and "men" as distinct tokens so a genuine men's vs women's
   * collection ("Men's Shorts" vs "Women's Shorts") is never silently dropped. Tokens are sorted so
   * word-order variants match; genuinely different titles ("High Support" vs "High Impact" Sports
   * Bras) keep distinct keys. Falls back to the literal title if tokenisation yields nothing.
   * @param {string} title
   * @returns {string}
   */
  #suggestionDedupeKey(title) {
    const canonical = title
      .toLowerCase()
      .replace(/['’]/g, '')
      .replace(/\b(womens|woman|ladies|lady)\b/g, 'women')
      .replace(/\b(mens|man)\b/g, 'men');
    const tokens = canonical.split(/[^a-z0-9]+/).filter(Boolean).sort();
    return tokens.join(' ') || title.toLowerCase().trim();
  }

  /**
   * Wrap every case-insensitive occurrence of `term` within `text` in <mark> (matching how
   * Shopify's styled_text bolds the match). Returns HTML-SAFE markup: the text is HTML-escaped
   * first, so it is safe to assign via innerHTML even though the source is provider catalog data.
   * @param {string} text
   * @param {string} term
   * @returns {string} HTML-escaped string with <mark>…</mark> around matches
   */
  #highlightTerm(text, term) {
    const escapeHtml = (s) =>
      s.replace(
        /[&<>"']/g,
        (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c])
      );
    const safeText = escapeHtml(text);
    const trimmed = (term ?? '').trim();
    if (!trimmed) return safeText;
    // Escape the term for HTML (so it aligns with safeText) then for regex (so specials are literal).
    const pattern = escapeHtml(trimmed).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    try {
      return safeText.replace(new RegExp(`(${pattern})`, 'gi'), '<mark>$1</mark>');
    } catch {
      return safeText;
    }
  }

  /**
   * Inject query-suggestion chips above the results meta row (provider or native source).
   * Mirrors the HTML the native predictive-search section produces for the Shopify
   * path so the same pill CSS and keyboard-nav ref selectors apply.
   * @param {Array<{text: string, styled_text: string, url: string}>} queries
   */
  #injectQuerySuggestions(queries) {
    const inner = this.querySelector('.predictive-search-results__inner');
    if (!inner) return;

    // Remove stale chips from a previous render (morph reconciles the section
    // markup but not these JS-injected nodes — they must be removed explicitly).
    inner.querySelector('.pw-provider-suggestions')?.remove();

    const label = this.dataset.querySuggestionsLabel ?? 'Search suggestions';

    const heading = document.createElement('h4');
    heading.className = 'predictive-search-results__title pw-search-suggestions__title';
    heading.textContent = `${label} (${queries.length})`;

    const ul = document.createElement('ul');
    ul.id = 'pw-query-listbox';
    ul.className =
      'predictive-search-results__list predictive-search-results__wrapper predictive-search-results__wrapper-queries list-unstyled';
    ul.setAttribute('role', 'listbox');
    ul.setAttribute('aria-label', label);

    queries.forEach((q, i) => {
      const li = document.createElement('li');
      li.className = 'predictive-search-results__card--query';
      li.setAttribute('role', 'option');
      li.setAttribute('aria-selected', 'false');
      li.setAttribute('id', `pw-pill-${i + 1}`);
      li.setAttribute('ref', 'resultsItems[]');
      // Parity with the native Liquid chip (sections/predictive-search.liquid) so injected and
      // server-rendered chips behave identically under the component's keydown delegation.
      li.setAttribute('on:keydown', '/onSearchKeyDown');

      const a = document.createElement('a');
      a.className = 'pills__pill predictive-search-results__pill';
      a.href = q.url;
      a.setAttribute('aria-label', q.text);
      // Clicking a chip re-runs the search in-panel (refine), rather than navigating to q.url.
      // The href stays a valid search URL so modified/middle clicks still open results in a new tab.
      a.addEventListener('click', (event) => this.#activateSuggestion(event, q.text));

      const span = document.createElement('span');
      // styled_text wraps the matched term in <mark>. Both sources are HTML-safe:
      // native = Shopify's own Predictive Search API; provider = #highlightTerm, which
      // HTML-escapes the text before inserting the <mark> tags.
      span.innerHTML = q.styled_text;

      a.appendChild(span);
      li.appendChild(a);
      ul.appendChild(li);
    });

    const wrapper = document.createElement('div');
    wrapper.className = 'pw-provider-suggestions';
    wrapper.append(heading, ul);

    // Place above the results-meta row (which #renderProviderChrome already inserted).
    const meta = inner.querySelector('.pw-search-meta');
    if (meta) {
      inner.insertBefore(wrapper, meta);
    } else {
      const status = inner.querySelector('#pw-results-status');
      inner.insertBefore(wrapper, status?.nextSibling ?? inner.firstChild);
    }
  }

  /**
   * Activate a suggestion chip: re-run the predictive search in-panel with the chip's term, instead
   * of navigating to its href (so the matching products update in place — the Oner pattern). A
   * modified click (cmd/ctrl/shift/alt) or non-primary button is left to the browser, so the href
   * (a search-results URL) still opens in a new tab.
   * @param {MouseEvent} event
   * @param {string} term - the chip's search term (the category title or native suggestion)
   */
  #activateSuggestion(event, term) {
    if (event.defaultPrevented) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button > 0) return;
    event.preventDefault();
    const input = this.refs.searchInput;
    if (!input) return;
    input.value = term;
    this.#currentIndex = -1;
    this.#showResetButton();
    input.focus();
    this.#getSearchResults(term);
  }

  /**
   * Fetch the markup for the recently viewed products.
   * @returns {Promise<string | null>} The markup for the recently viewed products.
   */
  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map(/** @param {string} id */ (id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #resetSearch = async () => {
    const { predictiveSearchResults, searchInput } = this.refs;
    const emptySectionId = 'predictive-search-empty';

    this.#currentIndex = -1;
    searchInput.value = '';
    this.#hideResetButton();

    const abortController = this.#createAbortController();
    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    let emptySectionMarkup;
    try {
      emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false, url);
    } catch (e) {
      // Section fetch failed (offline / 5xx): leave the current DOM rather than crashing the
      // reset, matching #renderProviderNoResults' fetch-failure discipline.
      return;
    }
    if (abortController.signal.aborted) return;

    const parsedEmptySectionMarkup = new DOMParser()
      .parseFromString(emptySectionMarkup, 'text/html')
      .querySelector('.predictive-search-empty-section');

    // No usable markup: leave the current DOM (was a throw → unhandled rejection).
    if (!parsedEmptySectionMarkup) return;

    const viewedProducts = RecentlyViewed.getProducts();

    if (viewedProducts.length > 0) {
      const recentlyViewedMarkup = await this.#getRecentlyViewedProductsMarkup();
      if (abortController.signal.aborted) return;

      if (recentlyViewedMarkup) {
        const parsedRecentlyViewedMarkup = new DOMParser().parseFromString(recentlyViewedMarkup, 'text/html');
        const recentlyViewedProductsHtml = parsedRecentlyViewedMarkup.getElementById('predictive-search-products');

        if (recentlyViewedProductsHtml) {
          const recentlyViewedSection = parsedEmptySectionMarkup.querySelector('#pw-recently-viewed');
          if (recentlyViewedSection) {
            recentlyViewedSection.appendChild(recentlyViewedProductsHtml);
            recentlyViewedSection.removeAttribute('hidden');
          }
        }
      }
    }

    if (abortController.signal.aborted) return;

    morph(predictiveSearchResults, parsedEmptySectionMarkup);
    this.#resetScrollPositions();

    // The morph above reconciled the Popular column back to its server-rendered curated chips.
    // Re-apply the cached worker terms so the dynamic chips survive close / query-clear (the
    // fetch only runs once per page-load). No-op until the worker terms have been fetched.
    this.#injectPopularSearches();
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
